import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import { buildExplorerRpcParams } from "@/pages/reports/sales-explorer/rpc-params";
import type {
  AppointmentRow,
  ItemRow,
  SalesExplorerFilters,
  SalesExplorerResult,
} from "@/pages/reports/sales-explorer/types";

export interface ExplorerRpcRow {
  result_format: string;
  result_rows: unknown;
  total_count: number | string;
  total_quantity: number | string;
  total_value: number | string;
  average_value: number | string;
}

const toNumber = (value: number | string): number => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export function mapExplorerResponse(row: ExplorerRpcRow): SalesExplorerResult {
  const rows = Array.isArray(row.result_rows) ? row.result_rows : [];
  const totals = {
    totalCount: toNumber(row.total_count),
    totalQuantity: toNumber(row.total_quantity),
    totalValue: toNumber(row.total_value),
    averageValue: toNumber(row.average_value),
  };

  if (row.result_format === "item") {
    return { format: "item", rows: rows as ItemRow[], ...totals };
  }

  return { format: "appointment", rows: rows as AppointmentRow[], ...totals };
}

export function salesExplorerQueryKey(
  clinicId: string | undefined,
  filters: SalesExplorerFilters,
): unknown[] {
  return ["sales-explorer", clinicId, buildExplorerRpcParams(filters)];
}

export function useSalesExplorer(
  filters: SalesExplorerFilters,
  options?: { enabled?: boolean },
) {
  const { data: clinicId } = useCurrentClinicId();

  return useQuery<SalesExplorerResult>({
    enabled: Boolean(clinicId) && Boolean(filters.from) && Boolean(filters.to) && options?.enabled !== false,
    queryKey: salesExplorerQueryKey(clinicId, filters),
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "search_sales_explorer",
        buildExplorerRpcParams(filters),
      );

      if (error) throw error;

      const first = data?.[0];
      if (!first) {
        return { format: "appointment", rows: [], totalCount: 0, totalQuantity: 0, totalValue: 0, averageValue: 0 };
      }

      return mapExplorerResponse(first);
    },
  });
}
